/**
 * Endpoint map — single source of truth for contract testing.
 *
 * Each registered Lambda endpoint maps to a stable `operationId` that
 * carries method / path / tag / Zod schemas for request + response.
 *
 * Two consumers use this map:
 *   1. Backend contract tests import handlers in-process, invoke them with a
 *      fake APIGatewayProxyEventV2 built from `requestBody`, and validate the
 *      resulting `LambdaProxyResponse.body` against `responseBody`.
 *   2. Frontend `apiFetch` helpers derive the response schema from the map to
 *      enforce the contract at call site (backwards from the legacy "pass a
 *      schema literal" approach).
 *
 * A sentinel test (`contract/parity.test.ts`) guarantees this map stays in
 * sync with `openapi.yaml` — any endpoint registered in `openapi/generate.ts`
 * but missing here (or vice versa) is a build-time error.
 */
import { z } from 'zod';
import { DataModelSchema } from '../entities/index.js';
import { CreateTenantRequestSchema, CreateTenantResponseSchema, ListTenantsResponseSchema, UpdateTenantRequestSchema, UpdateTenantResponseSchema, } from '../tenants/index.js';
import { AcceptInviteRequestSchema, AcceptInviteResponseSchema, InviteUserRequestSchema, InviteUserResponseSchema, ListUsersResponseSchema, UpdateUserRequestSchema, UpdateUserResponseSchema, } from '../users/index.js';
import { CreateConnectorRequestSchema, CreateConnectorResponseSchema, ListConnectorsResponseSchema, } from '../connectors/index.js';
import { CreateJobRequestSchema, CreateJobResponseSchema, ListJobsResponseSchema, } from '../jobs/index.js';
import { ApplyMigrationRequestSchema, ApplyMigrationResponseSchema, BroadcastRequestSchema, BroadcastResponseSchema, BulkPlanChangeRequestSchema, BulkPlanChangeResponseSchema, ChangeTenantPlanRequestSchema, ChangeTenantPlanResponseSchema, CreateDeletePlanRequestSchema, CreateDeletePlanResponseSchema, CreateTenantBackupRequestSchema, CreateTenantBackupResponseSchema, ExecuteDeletePlanResponseSchema, ExportTenantGdprRequestSchema, ExportTenantGdprResponseSchema, ImpersonateTenantRequestSchema, ImpersonateTenantResponseSchema, ListAuditLogResponseSchema, ListMigrationsResponseSchema, ListTenantBackupsResponseSchema, RestoreBackupAsNewRequestSchema, RestoreBackupAsNewResponseSchema, ResumeTenantRequestSchema, ResumeTenantResponseSchema, RevertMigrationRequestSchema, RevertMigrationResponseSchema, SuspendTenantRequestSchema, SuspendTenantResponseSchema, } from '../admin/index.js';
import { AnalyticsGlobalSearchResponseSchema, AnalyticsListResponseSchema, AnalyticsModelResponseSchema, AnalyticsQueryResponseSchema, AnalyticsResourceDetailResponseSchema, AnalyticsResponseSchema, QueryRequestSchema, AnalyticsQueryParamsSchema, } from '../analytics/index.js';
import { ChangePasswordRequestSchema, ChangePasswordResponseSchema, ForgotPasswordRequestSchema, ForgotPasswordResponseSchema, LoginRequestSchema, LoginResponseSchema, MeResponseSchema, RefreshRequestSchema, RefreshResponseSchema, ResetPasswordRequestSchema, ResetPasswordResponseSchema, } from '../auth/index.js';
// ============================================================================
// ANALYTICS (programmatic — many endpoints share a single contract)
// ============================================================================
/**
 * Analytics fact endpoints — single response contract ({@link AnalyticsResponseSchema}).
 *
 * Note: `/analytics/model` is intentionally **excluded** — it returns the raw
 * DataModel document, not the canonical KPI/trend envelope. See
 * `analytics.model` below.
 *
 * Also excluded (non-canonical shapes, registered explicitly further down):
 *   - `channels`, `warehouses`, `suppliers` — bare arrays (sidebar dropdowns)
 *   - `global-search` — grouped search hits
 *   - `detail` — drill-down, shape varies per `type` param
 *
 * Fact names MUST match the suffix keys in `handlers/analytics/src/index.ts`
 * route table.
 */
const ANALYTICS_FACTS = [
    'sales',
    'products',
    'brands',
    'categories',
    'customers',
    'geography',
    'rfm',
    'retention',
    'pnl',
    'seasonality',
    'smart-reorder',
    'inventory',
    'orders',
    'fulfillment',
    'supplier-orders',
    'catalog',
];
function analyticsEndpoints() {
    const out = {};
    for (const fact of ANALYTICS_FACTS) {
        const operationId = `analytics.${fact.replace(/-/g, '_')}`;
        out[operationId] = {
            operationId,
            method: 'get',
            path: `/analytics/${fact}`,
            tag: 'analytics',
            successStatus: 200,
            requiresAuth: true,
            queryParams: AnalyticsQueryParamsSchema,
            responseBody: AnalyticsResponseSchema,
        };
    }
    return out;
}
// ============================================================================
// MAP
// ============================================================================
/** Keep new endpoints in alphabetical order within each tag. */
export const ENDPOINT_MAP = {
    // ──── public (unauthenticated) ────────────────────────────────────────────
    'auth.login': {
        operationId: 'auth.login',
        method: 'post',
        path: '/public/login',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: false,
        requestBody: LoginRequestSchema,
        responseBody: LoginResponseSchema,
    },
    'auth.refresh': {
        operationId: 'auth.refresh',
        method: 'post',
        path: '/public/refresh',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: false,
        requestBody: RefreshRequestSchema,
        responseBody: RefreshResponseSchema,
    },
    'auth.forgotPassword': {
        operationId: 'auth.forgotPassword',
        method: 'post',
        path: '/public/forgot-password',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: false,
        requestBody: ForgotPasswordRequestSchema,
        responseBody: ForgotPasswordResponseSchema,
    },
    'auth.resetPassword': {
        operationId: 'auth.resetPassword',
        method: 'post',
        path: '/public/reset-password',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: false,
        requestBody: ResetPasswordRequestSchema,
        responseBody: ResetPasswordResponseSchema,
    },
    // ──── authenticated session ───────────────────────────────────────────────
    'auth.me': {
        operationId: 'auth.me',
        method: 'get',
        path: '/auth/me',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: true,
        responseBody: MeResponseSchema,
    },
    'auth.changePassword': {
        operationId: 'auth.changePassword',
        method: 'post',
        path: '/auth/change-password',
        tag: 'auth',
        successStatus: 200,
        requiresAuth: true,
        requestBody: ChangePasswordRequestSchema,
        responseBody: ChangePasswordResponseSchema,
    },
    // ──── tenants ─────────────────────────────────────────────────────────────
    'tenants.list': {
        operationId: 'tenants.list',
        method: 'get',
        path: '/tenants',
        tag: 'tenants',
        successStatus: 200,
        requiresAuth: true,
        responseBody: ListTenantsResponseSchema,
    },
    'tenants.create': {
        operationId: 'tenants.create',
        method: 'post',
        path: '/tenants',
        tag: 'tenants',
        successStatus: 201,
        requiresAuth: true,
        requestBody: CreateTenantRequestSchema,
        responseBody: CreateTenantResponseSchema,
    },
    'tenants.update': {
        operationId: 'tenants.update',
        method: 'patch',
        path: '/tenants/{tenantId}',
        tag: 'tenants',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: UpdateTenantRequestSchema,
        responseBody: UpdateTenantResponseSchema,
    },
    // ──── users ───────────────────────────────────────────────────────────────
    'users.list': {
        operationId: 'users.list',
        method: 'get',
        path: '/users',
        tag: 'users',
        successStatus: 200,
        requiresAuth: true,
        responseBody: ListUsersResponseSchema,
    },
    'users.update': {
        operationId: 'users.update',
        method: 'patch',
        path: '/users/{userId}',
        tag: 'users',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ userId: z.uuid() }),
        requestBody: UpdateUserRequestSchema,
        responseBody: UpdateUserResponseSchema,
    },
    'users.createInvitation': {
        operationId: 'users.createInvitation',
        method: 'post',
        path: '/users/invitations',
        tag: 'users',
        successStatus: 201,
        requiresAuth: true,
        requestBody: InviteUserRequestSchema,
        responseBody: InviteUserResponseSchema,
    },
    'users.acceptInvite': {
        operationId: 'users.acceptInvite',
        method: 'post',
        path: '/users/accept-invite',
        tag: 'users',
        successStatus: 201,
        requiresAuth: false,
        requestBody: AcceptInviteRequestSchema,
        responseBody: AcceptInviteResponseSchema,
    },
    // ──── connectors ──────────────────────────────────────────────────────────
    'connectors.list': {
        operationId: 'connectors.list',
        method: 'get',
        path: '/connectors',
        tag: 'connectors',
        successStatus: 200,
        requiresAuth: true,
        responseBody: ListConnectorsResponseSchema,
    },
    'connectors.create': {
        operationId: 'connectors.create',
        method: 'post',
        path: '/connectors',
        tag: 'connectors',
        successStatus: 201,
        requiresAuth: true,
        requestBody: CreateConnectorRequestSchema,
        responseBody: CreateConnectorResponseSchema,
    },
    // ──── jobs ────────────────────────────────────────────────────────────────
    'jobs.list': {
        operationId: 'jobs.list',
        method: 'get',
        path: '/jobs',
        tag: 'jobs',
        successStatus: 200,
        requiresAuth: true,
        responseBody: ListJobsResponseSchema,
    },
    'jobs.create': {
        operationId: 'jobs.create',
        method: 'post',
        path: '/jobs',
        tag: 'jobs',
        successStatus: 201,
        requiresAuth: true,
        requestBody: CreateJobRequestSchema,
        responseBody: CreateJobResponseSchema,
    },
    // ──── datamodel ───────────────────────────────────────────────────────────
    'datamodel.get': {
        operationId: 'datamodel.get',
        method: 'get',
        path: '/datamodel',
        tag: 'datamodel',
        successStatus: 200,
        requiresAuth: true,
        responseBody: DataModelSchema,
    },
    // ──── analytics ───────────────────────────────────────────────────────────
    // The analytics handler exposes N endpoints sharing identical contracts.
    // They are materialised below to keep the map fully enumerable.
    ...analyticsEndpoints(),
    'analytics.model': {
        operationId: 'analytics.model',
        method: 'get',
        path: '/analytics/model',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        queryParams: AnalyticsQueryParamsSchema,
        // /analytics/model is the odd one out — it returns the raw DataModel
        // document, not the canonical { kpis, trend, breakdowns, meta } envelope.
        responseBody: AnalyticsModelResponseSchema,
    },
    'analytics.query': {
        operationId: 'analytics.query',
        method: 'post',
        path: '/analytics/query',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        requestBody: QueryRequestSchema,
        // Generic dynamic-query endpoint returns a raw row envelope — NOT the
        // canonical { kpis, trend, breakdowns, meta } shape.
        responseBody: AnalyticsQueryResponseSchema,
    },
    // Non-canonical analytics endpoints — bare arrays powering sidebar dropdowns.
    'analytics.channels': {
        operationId: 'analytics.channels',
        method: 'get',
        path: '/analytics/channels',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        responseBody: AnalyticsListResponseSchema,
    },
    'analytics.warehouses': {
        operationId: 'analytics.warehouses',
        method: 'get',
        path: '/analytics/warehouses',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        responseBody: AnalyticsListResponseSchema,
    },
    'analytics.suppliers': {
        operationId: 'analytics.suppliers',
        method: 'get',
        path: '/analytics/suppliers',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        responseBody: AnalyticsListResponseSchema,
    },
    // Non-canonical — header search bar, grouped by dimension.
    'analytics.globalSearch': {
        operationId: 'analytics.globalSearch',
        method: 'get',
        path: '/analytics/global-search',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        queryParams: z.object({ q: z.string().optional() }),
        responseBody: AnalyticsGlobalSearchResponseSchema,
    },
    // Non-canonical — drill-down page, shape varies per `type` param.
    'analytics.detail': {
        operationId: 'analytics.detail',
        method: 'get',
        path: '/analytics/detail',
        tag: 'analytics',
        successStatus: 200,
        requiresAuth: true,
        queryParams: z.object({
            type: z.string().optional(),
            value: z.string().optional(),
            period: z.string().optional(),
            channel: z.string().optional(),
            warehouse: z.string().optional(),
            level: z.string().optional(),
        }),
        responseBody: AnalyticsResourceDetailResponseSchema,
    },
    // ──── admin (super_admin god mode) ───────────────────────────────────────
    'admin.tenant.suspend': {
        operationId: 'admin.tenant.suspend',
        method: 'post',
        path: '/admin/tenants/{tenantId}/suspend',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: SuspendTenantRequestSchema,
        responseBody: SuspendTenantResponseSchema,
    },
    'admin.tenant.resume': {
        operationId: 'admin.tenant.resume',
        method: 'post',
        path: '/admin/tenants/{tenantId}/resume',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: ResumeTenantRequestSchema,
        responseBody: ResumeTenantResponseSchema,
    },
    'admin.tenant.deletion.plan': {
        operationId: 'admin.tenant.deletion.plan',
        method: 'post',
        path: '/admin/tenants/{tenantId}/deletions',
        tag: 'admin',
        successStatus: 201,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: CreateDeletePlanRequestSchema,
        responseBody: CreateDeletePlanResponseSchema,
    },
    'admin.tenant.deletion.execute': {
        operationId: 'admin.tenant.deletion.execute',
        method: 'post',
        path: '/admin/tenants/{tenantId}/deletions/{planId}/execute',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid(), planId: z.uuid() }),
        responseBody: ExecuteDeletePlanResponseSchema,
    },
    'admin.tenant.plan.change': {
        operationId: 'admin.tenant.plan.change',
        method: 'patch',
        path: '/admin/tenants/{tenantId}/plan',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: ChangeTenantPlanRequestSchema,
        responseBody: ChangeTenantPlanResponseSchema,
    },
    'admin.tenant.impersonate': {
        operationId: 'admin.tenant.impersonate',
        method: 'post',
        path: '/admin/tenants/{tenantId}/impersonate',
        tag: 'admin',
        successStatus: 201,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: ImpersonateTenantRequestSchema,
        responseBody: ImpersonateTenantResponseSchema,
    },
    'admin.audit.list': {
        operationId: 'admin.audit.list',
        method: 'get',
        path: '/admin/audit',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        queryParams: z.object({
            action: z.string().optional(),
            actorId: z.uuid().optional(),
            tenantId: z.uuid().optional(),
            from: z.string().optional(),
            to: z.string().optional(),
            limit: z.coerce.number().int().positive().max(500).optional(),
            cursor: z.string().optional(),
        }),
        responseBody: ListAuditLogResponseSchema,
    },
    'admin.tenant.export.gdpr': {
        operationId: 'admin.tenant.export.gdpr',
        method: 'post',
        path: '/admin/tenants/{tenantId}/export-gdpr',
        tag: 'admin',
        successStatus: 201,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: ExportTenantGdprRequestSchema,
        responseBody: ExportTenantGdprResponseSchema,
    },
    'admin.tenant.backup.list': {
        operationId: 'admin.tenant.backup.list',
        method: 'get',
        path: '/admin/tenants/{tenantId}/backups',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        responseBody: ListTenantBackupsResponseSchema,
    },
    'admin.tenant.backup.create': {
        operationId: 'admin.tenant.backup.create',
        method: 'post',
        path: '/admin/tenants/{tenantId}/backups',
        tag: 'admin',
        successStatus: 201,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid() }),
        requestBody: CreateTenantBackupRequestSchema,
        responseBody: CreateTenantBackupResponseSchema,
    },
    'admin.tenant.backup.restoreAsNew': {
        operationId: 'admin.tenant.backup.restoreAsNew',
        method: 'post',
        path: '/admin/tenants/{tenantId}/backups/{backupId}/restore-as-new',
        tag: 'admin',
        successStatus: 201,
        requiresAuth: true,
        pathParams: z.object({ tenantId: z.uuid(), backupId: z.uuid() }),
        requestBody: RestoreBackupAsNewRequestSchema,
        responseBody: RestoreBackupAsNewResponseSchema,
    },
    'admin.migrations.master.list': {
        operationId: 'admin.migrations.master.list',
        method: 'get',
        path: '/admin/migrations/master',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        responseBody: ListMigrationsResponseSchema,
    },
    'admin.migrations.master.apply': {
        operationId: 'admin.migrations.master.apply',
        method: 'post',
        path: '/admin/migrations/master/apply',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        requestBody: ApplyMigrationRequestSchema,
        responseBody: ApplyMigrationResponseSchema,
    },
    'admin.migrations.master.revert': {
        operationId: 'admin.migrations.master.revert',
        method: 'post',
        path: '/admin/migrations/master/revert',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        requestBody: RevertMigrationRequestSchema,
        responseBody: RevertMigrationResponseSchema,
    },
    'admin.broadcast.send': {
        operationId: 'admin.broadcast.send',
        method: 'post',
        path: '/admin/broadcast',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        requestBody: BroadcastRequestSchema,
        responseBody: BroadcastResponseSchema,
    },
    'admin.plans.bulkChange': {
        operationId: 'admin.plans.bulkChange',
        method: 'post',
        path: '/admin/plans/bulk-change',
        tag: 'admin',
        successStatus: 200,
        requiresAuth: true,
        requestBody: BulkPlanChangeRequestSchema,
        responseBody: BulkPlanChangeResponseSchema,
    },
};
// ============================================================================
// LOOKUPS
// ============================================================================
/** Return the endpoint for a given method + path (path may be a template). */
export function findEndpoint(method, path) {
    const methodLower = method.toLowerCase();
    for (const ep of Object.values(ENDPOINT_MAP)) {
        if (ep.method === methodLower && ep.path === path)
            return ep;
    }
    return null;
}
/**
 * Match a concrete request URL (e.g. `/tenants/abc-123/settings`) against a
 * template path (e.g. `/tenants/{tenantId}/settings`). Returns the matching
 * endpoint def, or null.
 *
 * Used at the frontend fetch boundary — the client makes real requests and we
 * need to look up the endpoint by the actual URL, not the template.
 */
export function findEndpointByRequest(method, requestPath) {
    const methodLower = method.toLowerCase();
    // Strip query string if any — callers may pass a full URL path with `?q=...`.
    const pathOnly = requestPath.split('?')[0] ?? requestPath;
    for (const ep of Object.values(ENDPOINT_MAP)) {
        if (ep.method !== methodLower)
            continue;
        if (ep.path === pathOnly)
            return ep;
        if (!ep.path.includes('{'))
            continue;
        const regex = new RegExp('^' + ep.path.replace(/\{[^}]+\}/g, '[^/]+') + '$');
        if (regex.test(pathOnly))
            return ep;
    }
    return null;
}
/** Group endpoints by tag — useful for test-suite organisation. */
export function endpointsByTag() {
    const byTag = {};
    for (const ep of Object.values(ENDPOINT_MAP)) {
        const bucket = byTag[ep.tag] ?? [];
        bucket.push(ep);
        byTag[ep.tag] = bucket;
    }
    return byTag;
}
/** All operation IDs in registration order. */
export function allOperationIds() {
    return Object.keys(ENDPOINT_MAP);
}
//# sourceMappingURL=endpoint-map.js.map